import { useState, useCallback } from 'react';
import type React from 'react';
import type { ChangeEvent } from 'react';

import { List as MuiList } from '@mui/material';
import { JsonSchemaInput } from '@ragu-components/json-schema-input';
import { OrdDateTimeInput, OrdFormList, OrdIconStandard } from '@ragu-components/ord-components';
import { CalendarEventRruleFields, CalendarEventRruleUntilFields, dataSchema } from '@ragu-components/ord-types';
import { BooleanInput, required, SelectArrayInput, SelectInput, useResourceContext } from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';

import { booleanInputStyle } from 'src/lib/globalStyle';

import { dateInputValueParser } from '../utils';

import { frequencies, monthDays, monthNames, recurrenceTypes, weekdays } from './data';

import type { CalendarEventFields } from '../../types';
import type { RaRecord } from 'react-admin';

interface CalendarEventsRecurrenceFormProps {
  parentFields: CalendarEventFields;
}

export const CalendarEventsRecurrenceForm = ({
  parentFields,
}: CalendarEventsRecurrenceFormProps): React.ReactElement => {
  const { setValue, formState } = useFormContext();
  // The readonly dialog disables the form, but switches and pickers stay interactive
  // unless the flag reaches them explicitly
  const formDisabled = formState.disabled;
  const resource = useResourceContext() ?? 'calendar_events';
  const allDayInitial: boolean = useWatch({ name: 'allDay' });
  const allDay: boolean = useWatch({ name: parentFields.all_day, defaultValue: allDayInitial });
  const rrule = useWatch({ name: parentFields.rrule });
  const dtEnd = useWatch({ name: parentFields.dtend });
  const recurrenceEnabledSource = 'enableRrule';
  const recurrenceEnabled = useWatch({ name: recurrenceEnabledSource, defaultValue: rrule != null });
  const recurrenceTypeSource = 'recurrenceType';
  const rTypes = recurrenceTypes(resource);
  const freqs = frequencies(resource);
  const wDays = weekdays(resource);
  const mNames = monthNames(resource);
  const recurrenceType = useWatch({
    name: recurrenceTypeSource,
    defaultValue: rrule?.until != null ? rTypes[1]?.id : rTypes[0]?.id,
  });
  const [isCountedRecurrence, setIsCountedRecurrence] = useState(recurrenceType !== rTypes[1]?.id);

  const jsonSchema = isCountedRecurrence ? dataSchema.CalendarEventRrule : dataSchema.CalendarEventRruleUntil;
  const fields = isCountedRecurrence ? CalendarEventRruleFields : CalendarEventRruleUntilFields;

  const parser = useCallback(
    (valueFromInput: string | Date | null | undefined) => {
      return dateInputValueParser(valueFromInput, allDay);
    },
    [allDay]
  );

  const onRecurrenceEnableChange = useCallback(
    (event: ChangeEvent<HTMLInputElement> | RaRecord) => {
      if (event.target.checked === false) setValue(parentFields.rrule, null);
    },
    [parentFields.rrule, setValue]
  );

  const onRecurrenceTypeChange = useCallback(
    (event: ChangeEvent<HTMLInputElement> | RaRecord) => {
      const isCounted = event.target.value !== rTypes[1]?.id;
      const countDefault = isCounted ? dataSchema.CalendarEventRrule.properties.count.default : null;
      const untilDefault = !isCounted ? dtEnd : null;
      setIsCountedRecurrence(isCounted);
      setValue(`${parentFields.rrule}.${fields.count}`, countDefault);
      setValue(`${parentFields.rrule}.${fields.until}`, untilDefault);
    },
    [dtEnd, fields.count, fields.until, parentFields.rrule, rTypes, setValue]
  );

  return (
    <MuiList sx={{ width: '100%' }}>
      <OrdFormList icon={<OrdIconStandard name='eventRepeat' />}>
        <BooleanInput
          source={recurrenceEnabledSource}
          defaultValue={rrule != null}
          sx={booleanInputStyle}
          onChange={onRecurrenceEnableChange}
          disabled={formDisabled}
        />
      </OrdFormList>
      {recurrenceEnabled === true && (
        <OrdFormList icon={<></>}>
          <SelectInput
            choices={rTypes}
            source={recurrenceTypeSource}
            validate={required()}
            defaultValue={recurrenceType}
            onChange={onRecurrenceTypeChange}
          />
          <SelectInput
            choices={freqs}
            source={`${parentFields.rrule}.${fields.freq}`}
            validate={required()}
            defaultValue={freqs[0]?.id}
          />
          {isCountedRecurrence ? (
            <JsonSchemaInput
              jsonSchema={jsonSchema}
              property={fields.count}
              source={`${parentFields.rrule}.${fields.count}`}
            />
          ) : (
            <OrdDateTimeInput
              source={`${parentFields.rrule}.${fields.until}`}
              parse={parser}
              allDay={allDay}
              disabled={formDisabled}
            />
          )}
          <JsonSchemaInput
            jsonSchema={jsonSchema}
            property={fields.interval}
            source={`${parentFields.rrule}.${fields.interval}`}
          />
          <SelectArrayInput
            choices={wDays}
            source={`${parentFields.rrule}.${fields.byweekday}`}
            variant='outlined'
            defaultValue={dataSchema.CalendarEventRrule.properties.byweekday.default}
          />
          <SelectArrayInput
            choices={monthDays}
            source={`${parentFields.rrule}.${fields.bymonthday}`}
            variant='outlined'
            defaultValue={dataSchema.CalendarEventRrule.properties.bymonthday.default}
            translateChoice={false}
          />
          <SelectArrayInput
            choices={mNames}
            source={`${parentFields.rrule}.${fields.bymonth}`}
            variant='outlined'
            defaultValue={dataSchema.CalendarEventRrule.properties.bymonth.default}
          />
        </OrdFormList>
      )}
    </MuiList>
  );
};
